import * as React from 'react'
import styled from 'styled-components'

import * as glyphs from '../icons/glyphs/index'
import * as copy from '../styles/copy'
import * as S from '../styles/pagination'

const MAX_NUMBER_OF_PAGES_TO_DISPLAY = 4 // Excluding first and last, which always display.

type PageProps = {
  linkComponent?: (props: { children: React.ReactNode; page: number }) => React.ReactElement | null
  href?: (page: number) => string
  onNavigate?: (toPage: number) => void
  page: number
  selected: boolean
}

function Page({ linkComponent: Link, href, onNavigate, page, selected }: PageProps): React.ReactElement {
  const button = (
    <S.Button
      onClick={() => {
        if (onNavigate) {
          onNavigate(page)
        }
      }}
    >
      <copy.L>{page}</copy.L>
    </S.Button>
  )
  return (
    <S.Item selected={selected}>
      {Link && <Link page={page}>{button}</Link>}
      {!Link && (href ? <a href={href(page)}>{button}</a> : <>{button}</>)}
    </S.Item>
  )
}

export type PaginationProps = (
  | {
      href: (page: number) => string
      onNavigate?: (toPage: number) => void
    }
  | {
      href?: (page: number) => string
      onNavigate: (toPage: number) => void
    }
) & {
  className?: string
  arrowNavigation?: boolean
  linkComponent?: (props: { children: React.ReactNode; page: number }) => React.ReactElement | null
  // pageCount: Total number of pages to display
  pageCount: number
  selectedPage: number
  countTranslation?: string
} & React.OlHTMLAttributes<HTMLOListElement>

function PaginationComponent({
  arrowNavigation = true,
  linkComponent: Link,
  href,
  onNavigate,
  pageCount = 1,
  selectedPage = 1,
  countTranslation,
  className,
  ...olProps
}: PaginationProps): React.ReactElement {
  if (pageCount === 1) {
    return <ol className={className} {...olProps}></ol>
  }

  const pages = Array.from({ length: pageCount }, (v, k) => k + 1)

  const shouldSplitPagination = pageCount > MAX_NUMBER_OF_PAGES_TO_DISPLAY

  // Page number before which we may split
  const highestLimit = pageCount - MAX_NUMBER_OF_PAGES_TO_DISPLAY + 1
  // Page number after which we may split
  const lowestLimit = MAX_NUMBER_OF_PAGES_TO_DISPLAY
  // Difference between the two limits, which will be negative if you're displaying less pages than the allowed max.
  const range = highestLimit - lowestLimit

  const pageButtons = pages.reduce<JSX.Element[]>((pageAccumulator, index) => {
    const page = index

    if (shouldSplitPagination) {
      const isAtLimit = page === 1 || page === pageCount
      const pageIsADirectSiblingOfSelected = selectedPage === page - 1 || selectedPage === page + 1

      /**
       * We want to display a button for the currently processed page if it either is:
       * - the first or the last page;
       * - the currently selected page;
       * - a direct sibling of the currently selected page;
       * - lower than page n° after which we split AND the currently selected page is too;
       * - higher than page n° before which we split BUT the currently selected page is strictly too AND we're not going above the max number of pages to display;
       * - higher than page n° before which we split BUT the currently selected page is too AND we're showing less pages than the max allowed. (i.e. negative highest limit)
       */
      if (
        isAtLimit ||
        page === selectedPage ||
        pageIsADirectSiblingOfSelected ||
        (page < lowestLimit && selectedPage < lowestLimit) ||
        (range >= 0 && page > highestLimit && selectedPage > highestLimit) ||
        (range < 0 && page > highestLimit && selectedPage >= highestLimit)
      ) {
        pageAccumulator.push(
          <Page
            key={page}
            href={href}
            linkComponent={Link}
            onNavigate={onNavigate}
            page={page}
            selected={selectedPage === page}
          />,
        )
      } else if (
        /**
         * We want to display the ellipsis item for the currently processed page if it either is:
         * - at a distance of 2 from the currently selected page;
         * - is the lowest page at which we need to split AND the selected page is lower than it;
         * - is the highest page at which we need to split AND the selected page is higher than it.
         */
        page - selectedPage === 2 ||
        selectedPage - page === 2 ||
        (page === lowestLimit && selectedPage < lowestLimit) ||
        (page === highestLimit && selectedPage > highestLimit)
      ) {
        pageAccumulator.push(<S.EllipsisItem key={page} />)
      }

      // Skip all pages that are neither displayed nor being transformed to an ellipsis.
      return pageAccumulator
    }

    pageAccumulator.push(
      <Page
        key={page}
        href={href}
        linkComponent={Link}
        onNavigate={onNavigate}
        page={page}
        selected={selectedPage === page}
      />,
    )

    return pageAccumulator
  }, [])

  return (
    <S.Wrapper className={className}>
      {arrowNavigation && onNavigate !== undefined && (
        <Arrow linkComponent={Link} href={href} page={selectedPage - 1 >= 1 ? selectedPage - 1 : selectedPage}>
          <S.Arrow
            disabled={selectedPage <= 1}
            onClick={() => {
              if (selectedPage > 1) {
                onNavigate(selectedPage - 1)
              }
            }}
          >
            <glyphs.stroke.CaretDown size={13} />
          </S.Arrow>
        </Arrow>
      )}

      <ol {...olProps}>{pageButtons}</ol>

      {countTranslation && <S.Count>{countTranslation}</S.Count>}

      {arrowNavigation && onNavigate !== undefined && (
        <Arrow linkComponent={Link} href={href} page={selectedPage + 1 >= pageCount ? pageCount : selectedPage + 1}>
          <S.Arrow
            disabled={selectedPage >= pageCount}
            onClick={() => {
              if (selectedPage < pageCount) {
                onNavigate(selectedPage + 1)
              }
            }}
          >
            <glyphs.stroke.CaretUp size={13} />
          </S.Arrow>
        </Arrow>
      )}
    </S.Wrapper>
  )
}

type ArrowProps = {
  linkComponent?: (props: { children: React.ReactNode; page: number }) => React.ReactElement | null
  href?: (page: number) => string
  page: number
  children?: React.ReactNode
}

function Arrow({ linkComponent: Link, href, page, children }: ArrowProps): React.ReactElement {
  return (
    <>
      {Link && <Link page={page}>{children}</Link>}
      {!Link && (href ? <a href={href(page)}>{children}</a> : <>{children}</>)}
    </>
  )
}

export const Pagination = styled(PaginationComponent)<PaginationProps>``
Pagination.displayName = 'Pagination'
